import type { AnyFormApi } from '@tanstack/react-form';
import type { Dispatch, SetStateAction } from 'react';
import type { ContentTypeFormProperty } from '../../PropertyModalForm/helpers/parseProperty';

const isFormApi = (formApi: unknown): formApi is AnyFormApi => {
  return (
    typeof formApi === 'object' &&
    formApi !== null &&
    'getFieldValue' in formApi &&
    typeof formApi.getFieldValue === 'function' &&
    'setFieldValue' in formApi &&
    typeof formApi.setFieldValue === 'function'
  );
};

export const updateProperties = (
  formApi: unknown,
  property: ContentTypeFormProperty | undefined,
  newProperty: ContentTypeFormProperty,
  copiedPropertyIdx: number | undefined,
  metaPath: string,
  schemaPath: string,
  requiredBasePath: string,
  setDirty: Dispatch<SetStateAction<boolean>>,
) => {
  if (!isFormApi(formApi)) return;

  const required = formApi.getFieldValue(`${requiredBasePath}.required`) || [];
  const order = formApi.getFieldValue(`${metaPath}.order`);

  if (property && property.key !== newProperty.key) {
    formApi.setFieldValue(
      `${metaPath}.propertiesConfig.${property.key}`,
      undefined,
    );
    formApi.setFieldValue(
      `${schemaPath}.properties.${property.key}`,
      undefined,
    );

    const oldIdx = required.indexOf(property.key);
    required.splice(oldIdx, 1);
    const oldOrderIdx = order.indexOf(property.key);
    order.splice(oldOrderIdx, 1, newProperty.key);
  }

  formApi.setFieldValue(
    `${metaPath}.propertiesConfig.${newProperty.key}`,
    newProperty.config,
  );
  formApi.setFieldValue(
    `${schemaPath}.properties.${newProperty.key}`,
    newProperty.schema,
  );

  if (order?.indexOf(newProperty.key) < 0 && !property) {
    if (copiedPropertyIdx != null && copiedPropertyIdx > -1)
      order.splice(copiedPropertyIdx + 1, 0, newProperty.key);
    else order.push(newProperty.key);
  }

  formApi.setFieldValue(`${metaPath}.order`, order);

  const requiredIdx = required?.indexOf(newProperty.key);
  if (newProperty.required) {
    if (requiredIdx < 0) required.push(newProperty.key);
  } else {
    if (requiredIdx > -1) required.splice(requiredIdx, 1);
  }

  formApi.setFieldValue(`${requiredBasePath}.required`, required);
  setDirty(true);
};

export const updateOrder = (
  sourceName: string,
  targetName: string,
  formApi: unknown,
  orderBasePath: string,
  setDirty: Dispatch<SetStateAction<boolean>>,
) => {
  if (!isFormApi(formApi)) return;

  if (sourceName === targetName) return;
  const order = formApi.getFieldValue(`${orderBasePath}.order`) as string[];
  if (!order || order.length < 2) return;

  const targetIdx = order.findIndex((name) => name === targetName);
  const sourceIdx = order.findIndex((name) => name === sourceName);
  const newOrder = [...order];
  newOrder.splice(targetIdx, 0, newOrder.splice(sourceIdx, 1)[0]);

  formApi.setFieldValue(`${orderBasePath}.order`, newOrder);
  setDirty(true);
};

export const changeOrderUp = (
  formApi: unknown,
  orderBasePath: string,
  idx: number,
  setDirty: Dispatch<SetStateAction<boolean>>,
) => {
  if (!isFormApi(formApi)) return;

  const newOrder = formApi.getFieldValue(`${orderBasePath}.order`);
  newOrder.splice(idx - 1, 0, newOrder.splice(idx, 1)[0]);

  formApi.setFieldValue(`${orderBasePath}.order`, newOrder);
  setDirty(true);
};

export const changeOrderDown = (
  formApi: unknown,
  orderBasePath: string,
  idx: number,
  setDirty: Dispatch<SetStateAction<boolean>>,
) => {
  if (!isFormApi(formApi)) return;

  const newOrder = formApi.getFieldValue(`${orderBasePath}.order`);
  newOrder.splice(idx + 1, 0, newOrder.splice(idx, 1)[0]);

  formApi.setFieldValue(`${orderBasePath}.order`, newOrder);
  setDirty(true);
};

export const deleteProperty = (
  formApi: unknown,
  fieldName: string,
  metaPath: string,
  schemaPath: string,
  requiredBasePath: string,
  setDirty: Dispatch<SetStateAction<boolean>>,
) => {
  if (!isFormApi(formApi)) return;

  const required = (formApi.getFieldValue(`${requiredBasePath}.required`) ||
    []) as string[];
  const order = formApi.getFieldValue(`${metaPath}.order`) as string[];

  formApi.setFieldValue(
    `${metaPath}.order`,
    order.filter((element) => element !== fieldName),
  );
  formApi.setFieldValue(
    `${requiredBasePath}.required`,
    required.filter((element) => element !== fieldName),
  );

  formApi.setFieldValue(`${metaPath}.propertiesConfig.${fieldName}`, undefined);
  formApi.setFieldValue(`${schemaPath}.properties.${fieldName}`, undefined);
  setDirty(true);
};
